import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useToast } from 'context/providers/ToastProvider';

const ModalMapeoCSV = ({
    show,
    onHide,
    onConfirm,
    datosCSV,
    mapeoColumnas,
    setMapeoColumnas,
    cuentasBancarias,
    cuentaSeleccionada,
    setCuentaSeleccionada,
    tipoMovimientoDefault,
    setTipoMovimientoDefault
}) => {
    const { showErrorToast } = useToast();
    
    const handleConfirmar = () => {
        // Validar que todos los campos obligatorios estén mapeados
        const camposObligatorios = ['identificador', 'fecha', 'descripcion', 'monto', 'referencia'];
        const camposFaltantes = camposObligatorios.filter(campo => !mapeoColumnas[campo]);
        
        if (camposFaltantes.length > 0) {
            showErrorToast(`Debe mapear los siguientes campos obligatorios: ${camposFaltantes.join(', ')}`);
            return;
        }

        if (!cuentaSeleccionada) {
            showErrorToast('Debe seleccionar una cuenta bancaria');
            return;
        }

        onConfirm();
    };

    return (
        <Modal 
            show={show} 
            onHide={onHide} 
            size="xl"
            backdrop="static"
        >
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>
                    <i className="fas fa-table me-2"></i>
                    Configurar Mapeo de Columnas CSV
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="alert alert-info fs--1">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Instrucciones:</strong> Configure los datos de importación y seleccione las columnas correspondientes de su archivo CSV.
                    Los campos marcados con <span className="text-danger">*</span> son obligatorios.
                </div>

                {/* Configuración General */}
                <Card className="mb-3 border-primary">
                    <Card.Header className="bg-light">
                        <strong><i className="fas fa-cog me-2"></i>Configuración General de Importación</strong>
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold fs--1">
                                        <span className="text-danger">*</span> Cuenta Bancaria
                                    </Form.Label>
                                    <Form.Select
                                        value={cuentaSeleccionada}
                                        onChange={(e) => setCuentaSeleccionada(e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="">-- Seleccione cuenta bancaria --</option>
                                        {cuentasBancarias.map((cuenta) => (
                                            <option key={cuenta.idCatCuentaBancaria} value={cuenta.idCatCuentaBancaria}>
                                                {cuenta.vNombreCuenta} - {cuenta.vNumeroCuenta}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted fs--2">
                                        Seleccione la cuenta bancaria a la que pertenecen estos movimientos
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold fs--1">
                                        Tipo de Movimiento por Defecto
                                    </Form.Label>
                                    <Form.Select
                                        value={tipoMovimientoDefault}
                                        onChange={(e) => setTipoMovimientoDefault(e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="">-- Detectar automáticamente --</option>
                                        <option value="ingreso">Todos son Ingresos</option>
                                        <option value="egreso">Todos son Egresos</option>
                                    </Form.Select>
                                    <Form.Text className="text-muted fs--2">
                                        Si no hay columna de tipo, todos los movimientos serán de este tipo
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Row className="mb-4">
                    <Col md={6}>
                        <Card className="border">
                            <Card.Header className="bg-light">
                                <strong>Mapeo de Columnas Obligatorias</strong>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold fs--1">
                                        <span className="text-danger">*</span> Identificador/ID
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={mapeoColumnas.identificador}
                                        onChange={(e) => setMapeoColumnas({...mapeoColumnas, identificador: e.target.value})}
                                    >
                                        <option value="">-- Seleccione columna --</option>
                                        {datosCSV.headers.map((header, idx) => (
                                            <option key={idx} value={idx}>{header}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted fs--2">
                                        ID único del movimiento bancario
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold fs--1">
                                        <span className="text-danger">*</span> Fecha
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={mapeoColumnas.fecha}
                                        onChange={(e) => setMapeoColumnas({...mapeoColumnas, fecha: e.target.value})}
                                    >
                                        <option value="">-- Seleccione columna --</option>
                                        {datosCSV.headers.map((header, idx) => (
                                            <option key={idx} value={idx}>{header}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted fs--2">
                                        Fecha del movimiento
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold fs--1">
                                        <span className="text-danger">*</span> Descripción
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={mapeoColumnas.descripcion}
                                        onChange={(e) => setMapeoColumnas({...mapeoColumnas, descripcion: e.target.value})}
                                    >
                                        <option value="">-- Seleccione columna --</option>
                                        {datosCSV.headers.map((header, idx) => (
                                            <option key={idx} value={idx}>{header}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted fs--2">
                                        Descripción o concepto del movimiento
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold fs--1">
                                        <span className="text-danger">*</span> Monto
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={mapeoColumnas.monto}
                                        onChange={(e) => setMapeoColumnas({...mapeoColumnas, monto: e.target.value})}
                                    >
                                        <option value="">-- Seleccione columna --</option>
                                        {datosCSV.headers.map((header, idx) => (
                                            <option key={idx} value={idx}>{header}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted fs--2">
                                        Importe del movimiento
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold fs--1">
                                        <span className="text-danger">*</span> Referencia
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={mapeoColumnas.referencia}
                                        onChange={(e) => setMapeoColumnas({...mapeoColumnas, referencia: e.target.value})}
                                    >
                                        <option value="">-- Seleccione columna --</option>
                                        {datosCSV.headers.map((header, idx) => (
                                            <option key={idx} value={idx}>{header}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted fs--2">
                                        Número de referencia bancaria
                                    </Form.Text>
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card className="border">
                            <Card.Header className="bg-light">
                                <strong>Campos Opcionales</strong>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold fs--1">
                                        Tipo de Movimiento
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={mapeoColumnas.tipo}
                                        onChange={(e) => setMapeoColumnas({...mapeoColumnas, tipo: e.target.value})}
                                    >
                                        <option value="">-- Seleccione columna --</option>
                                        {datosCSV.headers.map((header, idx) => (
                                            <option key={idx} value={idx}>{header}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted fs--2">
                                        Debe contener: "ingreso", "egreso", "cargo", "abono", "C", "D", etc.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold fs--1">
                                        Moneda
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={mapeoColumnas.moneda}
                                        onChange={(e) => setMapeoColumnas({...mapeoColumnas, moneda: e.target.value})}
                                    >
                                        <option value="">-- Seleccione columna --</option>
                                        {datosCSV.headers.map((header, idx) => (
                                            <option key={idx} value={idx}>{header}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted fs--2">
                                        Si no se especifica, se asumirá MXN
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold fs--1">
                                        Tipo de Cambio
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={mapeoColumnas.tipoCambio}
                                        onChange={(e) => setMapeoColumnas({...mapeoColumnas, tipoCambio: e.target.value})}
                                    >
                                        <option value="">-- Seleccione columna --</option>
                                        {datosCSV.headers.map((header, idx) => (
                                            <option key={idx} value={idx}>{header}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted fs--2">
                                        Para monedas distintas a MXN
                                    </Form.Text>
                                </Form.Group>

                                <div className="alert alert-warning mt-4 fs--2 py-2">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    <strong>Nota:</strong> Si no se mapea el "Tipo de Movimiento", se usará el tipo por defecto seleccionado arriba.
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Card>
                    <Card.Header className="bg-light">
                        <strong>Vista Previa de Datos (primeras 5 filas)</strong>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div style={{ maxHeight: '300px', overflowX: 'auto', overflowY: 'auto' }}>
                            <Table striped bordered hover size="sm" className="mb-0">
                                <thead className="bg-200 sticky-top">
                                    <tr>
                                        {datosCSV.headers.map((header, idx) => (
                                            <th key={idx} className="fs--2 text-nowrap">
                                                {header}
                                                <br />
                                                <Badge bg="secondary" className="fs--2">
                                                    Col {idx}
                                                </Badge>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {datosCSV.rows.map((row, rowIdx) => (
                                        <tr key={rowIdx}>
                                            {row.map((cell, cellIdx) => (
                                                <td key={cellIdx} className="fs--2 text-nowrap">
                                                    {cell}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                    <Card.Footer className="bg-light fs--2 text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        Total de registros en el archivo: {datosCSV.rows.length + 1} (incluyendo encabezado)
                    </Card.Footer>
                </Card>
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={onHide}>
                    <i className="fas fa-times me-1"></i>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleConfirmar}>
                    <i className="fas fa-check me-1"></i>
                    Confirmar e Importar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalMapeoCSV.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    datosCSV: PropTypes.shape({
        headers: PropTypes.arrayOf(PropTypes.string).isRequired,
        rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired
    }).isRequired,
    mapeoColumnas: PropTypes.shape({
        identificador: PropTypes.string,
        fecha: PropTypes.string,
        descripcion: PropTypes.string,
        monto: PropTypes.string,
        tipo: PropTypes.string,
        referencia: PropTypes.string,
        moneda: PropTypes.string,
        tipoCambio: PropTypes.string
    }).isRequired,
    setMapeoColumnas: PropTypes.func.isRequired,
    cuentasBancarias: PropTypes.arrayOf(
        PropTypes.shape({
            idCatCuentaBancaria: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            vNombreCuenta: PropTypes.string.isRequired,
            vNumeroCuenta: PropTypes.string.isRequired,
            vBanco: PropTypes.string,
            vMoneda: PropTypes.string,
            vTipoCuenta: PropTypes.string
        })
    ).isRequired,
    cuentaSeleccionada: PropTypes.string.isRequired,
    setCuentaSeleccionada: PropTypes.func.isRequired,
    tipoMovimientoDefault: PropTypes.string.isRequired,
    setTipoMovimientoDefault: PropTypes.func.isRequired
};



ModalMapeoCSV.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    onConfirm: PropTypes.func,
    datosCSV: PropTypes.string,
    mapeoColumnas: PropTypes.array,
    setMapeoColumnas: PropTypes.func,
    cuentasBancarias: PropTypes.array,
    cuentaSeleccionada: PropTypes.string,
    setCuentaSeleccionada: PropTypes.func,
    tipoMovimientoDefault: PropTypes.string,
    setTipoMovimientoDefault: PropTypes.func
};

export default ModalMapeoCSV;
